<template>
  <div class='wrapper-page'>
    <modal ref="restoreMessageForm">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.restore_message')}}
        </h5>
      </template>
      <template #body>
        <form>
          <div class='modal-body'>
            <p>{{ $t('communication.are_you_sure_you_wanna_restore')}}</p>
          </div>
          <div class='modal-footer'>
            <input :value="$t('communication.restore_messages_button')" @click.prevent='restoreMessages()' class='btn btn-primary float-right' type='reset' />
          </div>
        </form>
      </template>
    </modal>

    <modal ref='filtersModal' class="filter-all-messages">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('shared.filter') }}
        </h5>
      </template>

      <template #body>
        <form @submit.prevent='filterTrash'>
          <div class='modal-body'>
            <div class="form-group">
              <label>{{ $t('communication.deleted_by_user') }}</label>
              <infinite-multi-select :getter="getUsers" v-model="deletedByUserFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.contact_name') }}</label>
              <infinite-multi-select :getter="getContacts" v-model="contactFilters" />
            </div>
            <div class="form-group">
              <label>
                {{ $t('communication.message_type') }}
              </label>
              <multi-select :options="messageType" v-model="messageTypeFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.subject') }}</label>
              <input type="text" name="search" v-model="searchInSubject" class="form-control">
            </div>
            <div class="form-group">
              <label>{{ $t('communication.sent_date') }}</label>
              <div class="date">
                <label>{{ $t('communication.from') }}</label>
                <datepicker
                  ref="datepicker"
                  name="trash[delete_date]"
                  v-model="fromReceivedDate"
                  input-class="form-control expiration-input"
                  :inline="false"
                  :typeable="true"
                  :full-month-name="true"
                  :monday-first="true"
                  :placeholder="$t('expiration.pick_date')">
                </datepicker>
              </div>
              <div class="date">
                <label>{{ $t('communication.to') }}</label>
                <datepicker
                  ref="datepicker"
                  name="trash[deleted_date]"
                  v-model="toReceivedDate"
                  input-class="form-control expiration-input"
                  :inline="false"
                  :typeable="true"
                  :full-month-name="true"
                  :monday-first="true"
                  @selected="this.$refs.datepicker.close()"
                  :placeholder="$t('expiration.pick_date')">
                </datepicker>
              </div>
            </div>
          </div>

          <div class='modal-footer'>
            <input type='reset' class='btn btn-neutral float-right' :value="$t('folders_list.clear_filters')" @click.prevent='resetFilters' />
            <input type='submit' class='btn btn-outline-primary float-right' :value="$t('folders_list.filter')" />
          </div>
        </form>
      </template>
    </modal>

    <div class='container-fluid text-center'>
      <h1 class='page-title'>
        {{ $t('communication.trash') }} ({{meta.total}})
        <a :href="trashVideo()" target="_blank" class="own-tooltip own-tooltip-bottom video-link" :data-tooltip="$t('communication.click_here_to_watch_trash')"><img src="../../assets/video.svg" /></a>
      </h1>
      <div class="actions-on-table">
        <div class='filter'>
          <a  @click.prevent="openModal($refs.filtersModal)" href="#" class="filter-by btn btn-neutral">
            {{ $t('root.filter_all_data_by') }}
          </a>
        </div>
      </div>
      <div class="filter-results" v-if="filterMessages">
        <strong v-if="itHasFilters">{{ $t('root.filter_results_for') }}</strong>
        <div v-if="deletedByUserFilters.length > 0">
          <p>{{ $t('communication.filters.from') }} <strong v-for="user in deletedByUserFilters">{{user.name}}</strong></p>
        </div>
        <div v-if="contactFilters.length > 0">
          <p>{{ $t('communication.filters.to') }} <strong v-for="contact in contactFilters">{{contact.name}}</strong></p>
        </div>
        <div v-if="messageTypeFilters.length > 0">
          <p>{{ $t('communication.filters.message_type') }} <strong v-for="type in messageTypeFilters">{{type.name}}</strong></p>
        </div>
        <div v-if="searchInSubject !== ''">
          <p>{{ $t('communication.filters.subject') }} <strong>{{searchInSubject}}</strong></p>
        </div>
        <div v-if="fromReceivedDate !== null">
          <p>{{ $t('communication.filters.receiving_date') }} <strong>{{formatDate(fromReceivedDate)}}</strong></p>
        </div>
        <div v-if="toReceivedDate !== null">
          <p>{{ $t('communication.filters.until_date') }} <strong>{{formatDate(toReceivedDate)}}</strong></p>
        </div>

        <a v-if="itHasFilters" @click.prevent="resetFilters" href="#">{{ $t('records_list.clear_filters') }}</a>
      </div>
      <loading v-if="loading" />
      <div v-else-if='trashEmails.length === 0 && !loading' class="empty-list-wrapper">
        <img src="../../assets/empty_states/envelope.svg" />
        <p>{{$t('communication.sent_empty_state')}}</p>
      </div>
      <div class="table_wrapper" v-else-if='trashEmails.length > 0 && !loading'>
        <div :class="['bulk-container', { 'hide': !isBulkEnabled  }]">
          <span>{{ bulkOpMessages.length }} {{ $t('root.messages') }}</span>
          <ul>
            <li>
              <a @click.prevent='restoreMessages()' href="#" rel="nofollow" class="dropdown-item">
                <i class="far fa-trash-alt"></i>
                {{ $t('communication.restore')}}
              </a>
            </li>
          </ul>
        </div>
        <table class="table">
          <thead>
          <tr>
            <th><input type='checkbox' @input='selectAll' /></th>
            <th>{{ $t('communication.message_type')}}</th>
            <th>{{ $t('communication.contact_name')}}</th>
            <th>{{ $t('communication.subject')}}</th>
            <th>{{ $t('communication.date')}}</th>
            <th>{{ $t('communication.deleted_by_user')}}</th>
            <th>{{ $t('communication.actions')}}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in trashEmails">
            <td class="bulk-check">
              <input type='checkbox' :value='item.messageable_id' v-model='bulkOpRequest' v-if="item.messageable_type === 'DocumentRequest'" />
              <input type='checkbox' :value='item.messageable_id' v-model='bulkOpResponse' v-else-if="item.messageable_type === 'DocumentResponse'" />
              <input type='checkbox' :value='item.messageable_id' v-model='bulkOpAutomated' v-else-if="item.messageable_type === 'SentEmail'" />
              <input type='checkbox' :value='item.messageable_id' v-model='bulkOpRecordMessage' v-else-if="item.messageable_type === 'RecordMessage'" />
            </td>
            <td class="search-icon-column">
                <span v-if="item.messageable_type === 'DocumentRequest'"
                      :data-tooltip="$t('communication.tooltip.sent_message')" class="own-tooltip own-tooltip-bottom">
                    <img src="../../assets/search/sent-mail.svg" />
                </span>

              <span v-if="item.messageable_type === 'DocumentResponse'"
                    :data-tooltip="$t('communication.tooltip.received_message')" class="own-tooltip own-tooltip-bottom">
                  <img src="../../assets/search/incoming-mail.svg" />
              </span>

              <span v-if="item.messageable_type === 'SentEmail'"
                    :data-tooltip="$t('communication.tooltip.automated_email')" class="own-tooltip own-tooltip-bottom">
                  <img src="../../assets/search/message.svg" />
              </span>

              <span v-if="item.messageable_type === 'RecordMessage'"
                    :data-tooltip="$t('communication.tooltip.file_communication')" class="own-tooltip own-tooltip-bottom">
                  <img src="../../assets/search/internal_message.svg" />
              </span>
            </td>
            <td>
              <router-link :to="{name: 'message', params: { id: item.messageable_id, referrer: $route.name, messageType: $route.name }, query: { type: queryType(item.messageable_type) }}" v-if="item.contact !== undefined">
                <span v-if="Object.keys(item.contact).length > 0">
                  {{ item.contact.name }}
                <span v-if="item.contact.name && item.contact.email"> - </span>
                  {{ item.contact.email }}
                </span>
              </router-link>
              <router-link :to="{name: 'record_message_show', params: { id: item.messageable_id, referrer: $route.name }, query: { type: 'trash' }}">
                  {{ sentTo(item) }}
              </router-link>
            </td>
            <td>
              <span v-if="item.subject">{{ item.subject }}</span>
              <span v-else>{{ $t('communication.no_subject')}}</span>
            </td>
            <td>{{ formatDate(item.created_at) }}</td>
            <td>
                <span v-if="item.deleted_by_user.full_name">
                  {{ item.deleted_by_user.full_name }}
                </span>
                <span v-else-if="item.deleted_by_user.email">
                  {{ item.deleted_by_user.email }}
                </span>
            </td>
            <td class="dropdown-column">
              <div class="dropdown">
                <button aria-expanded="false" aria-haspopup="true" class="btn dropdown-toggle" data-toggle="dropdown" type="button">· · ·</button>
                <div class="dropdown-menu dropdown-menu-right">
                  <ul>
                    <li>
                      <a @click.prevent='restoreMessage(item)' class="dropdown-item" href="#" rel="nofollow">
                        <i class="fas fa-trash-restore"></i>
                        {{ $t('communication.restore')}}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <pagination :meta='meta'/>
    </div>
  </div>
</template>

<script>
  import _modal from '../../components/_modal'
  import _pagination from "../../components/_pagination";
  import _loading from '../../components/_loading'
  import _multi_select from "../../components/shared/_multi_select"
  import _infinite_multi_select from "../../components/shared/_infinite_multi_select"
  import Datepicker from 'vuejs-datepicker'

  export default {
    name: 'Trash',
    components: {
      'multi-select': _multi_select,
      'infinite-multi-select': _infinite_multi_select,
      'modal': _modal,
      pagination: _pagination,
      'loading': _loading,
      'datepicker': Datepicker,
    },
    watch: {
      query() {
        this.getTrashEmails()
      }
    },
    data() {
      return {
        loading: true,
        bulkOpRequest: [],
        bulkOpResponse: [],
        bulkOpAutomated: [],
        bulkOpRecordMessage: [],
        itHasFilters: false,
        loaded: {},
        currentMessage: null,
        searchInTrash: '',
        messageTypeFilters: [],
        searchInSubject: '',
        deletedByUserFilters: [],
        contactFilters: [],
        fromReceivedDate: null,
        toReceivedDate: null
      }
    },
    created() {
      this.getTrashEmails()
      this.store.sendMetrics({name: 'communication_trash', params: this.$route.params, path: this.$route.fullPath})
    },
    computed: {
      meta () {
        return this.state.meta || {}
      },
      trashEmails() {
        return this.state.trashEmails || []
      },
      bulkOpMessages() {
        return this.bulkOpRequest.concat(this.bulkOpResponse, this.bulkOpAutomated, this.bulkOpRecordMessage)
      },
      isBulkEnabled() {
        return this.bulkOpMessages.length > 0
      },
      messageType() {
        return [
          { id: 'SentEmail', name: this.$t('communication.automated_emails') },
          { id: 'DocumentRequest', name: this.$t('communication.sent_messages') },
          { id: 'DocumentResponse', name: this.$t('communication.received_messages') },
          { id: 'RecordMessage', name: this.$t('communication.file_communications') },
        ]
      }
    },

    methods: {
      getTrashEmails() {
        this.loading = true
        this.store.getTrashEmails(this.query).then(() =>  this.loading = false)
      },
      openModal(modal) {
        modal.open()
      },
      sentTo(message){
        let list = []
        let currentOrg = this.currentOrg

        if(message.sent_to_contacts !== undefined){
          message.sent_to_contacts.map( (item) => {
            if(item.id !== undefined)
                list.push(this.formatName(item.name, item.email))
          })
        }
        if(message.sent_to_users !== undefined){
          message.sent_to_users.map( (item) => {
            if(item.id !== undefined)
                list.push(this.formatName(item.full_name, item.email))
          })
        }

        return list.join(', ')
      },
      formatName(name, email){
        if(email)
          return `${name} <${email}>`
        else
          return name
      },
      filterMessages() {
        return this.contactFilters.length > 0 || this.deletedByUserFilters.length > 0 ||
          this.messageTypeFilters.length > 0 || this.fromReceivedDate !== null ||
          this.toReceivedDate !== null || this.searchInSubject !== ''
      },
      filterTrash() {
        let q  = {
          'filters[users]': this.deletedByUserFilters.length > 0 ? this.deletedByUserFilters.map(c => c.id) : null,
          'filters[contacts]': this.contactFilters.length > 0 ? this.contactFilters.map(c => c.id) : null,
          'filters[types]': this.messageTypeFilters.length > 0 ? this.messageTypeFilters.map(c => c.id) : null,
          'filters[subject]': this.searchInSubject.length > 2 ? this.searchInSubject : null,
          'filters[from_received_date]': this.fromReceivedDate !== null ? this.formatDate(this.fromReceivedDate) : null,
          'filters[to_received_date]': this.toReceivedDate !== null ? this.formatDate(this.toReceivedDate) : null,
          'page': this.query.page || 1
        }
        const f = Object.entries(q).reduce((a,[k,v]) => (v === null ? a : (a[k]= v, a)), {})

        this.$refs.filtersModal.close()
        this.itHasFilters = true
        this.$router.push({ query: f })
      },
      selectAll(e) {
        this.bulkOpRequest = e.target.checked ? this.trashEmails.filter(items => items.messageable_type == 'DocumentRequest').map(i => i.messageable_id) : []
        this.bulkOpResponse = e.target.checked ? this.trashEmails.filter(items => items.messageable_type == 'DocumentResponse').map(i => i.messageable_id) : []
        this.bulkOpAutomated = e.target.checked ? this.trashEmails.filter(items => items.messageable_type == 'SentEmail').map(i => i.messageable_id) : []
        this.bulkOpRecordMessage = e.target.checked ? this.trashEmails.filter(items => items.messageable_type == 'RecordMessage').map(i => i.messageable_id) : []
      },
      closeDatePicker() {
        this.$refs.datepicker.close()
      },
      filtersOn() {
        this.itHasFilters = true
        this.$refs.filtersModal.close()
      },
      resetFilters() {
        this.itHasFilters = false
        this.deletedByUserFilters = []
        this.contactFilters= []
        this.messageTypeFilters = []
        this.searchInSubject = ''
        this.fromReceivedDate = null
        this.toReceivedDate = null
        this.$router.push({ query: null})
        this.$refs.filtersModal.close()
      },
      restoreMessage(item) {
        this.currentMessage = item
        this.$refs.restoreMessageForm.open()
      },
      restoreMessages() {
        this.store.sendMetrics({name: 'communication_restore_bin', params: this.$route.params, path: this.$route.fullPath})

        this.$refs.restoreMessageForm.close()

        this.loading = true
        if (this.currentMessage) {
          let promise = null

          if (this.currentMessage.messageable_type == 'DocumentRequest')
            promise = this.store.restoreDocumentRequest([this.currentMessage.messageable_id])

          if (this.currentMessage.messageable_type == 'DocumentResponse')
            promise = this.store.restoreDocumentResponse([this.currentMessage.messageable_id])

          if (this.currentMessage.messageable_type == 'SentEmail')
            promise = this.store.restoreAutomatedEmail([this.currentMessage.messageable_id])

          if (this.currentMessage.messageable_type == 'RecordMessage')
            promise = this.store.restoreRecordMessage([this.currentMessage.messageable_id])

          promise.then(() => {
            this.getTrashEmails()

            this.currentMessage = null
          })
        } else {
          let promises = []

          if (this.bulkOpRequest.length > 0)
            promises.push(this.store.restoreDocumentRequest(this.bulkOpRequest))

          if (this.bulkOpResponse.length > 0)
            promises.push(this.store.restoreDocumentResponse(this.bulkOpResponse))

          if (this.bulkOpAutomated.length > 0)
            promises.push(this.store.restoreAutomatedEmail(this.bulkOpAutomated))

          if (this.bulkOpRecordMessage.length > 0)
            promises.push(this.store.restoreRecordMessage(this.bulkOpRecordMessage))

          Promise.allSettled(promises).then(() => {
            this.getTrashEmails()

            this.bulkOpRequest = []
            this.bulkOpResponse = []
            this.bulkOpAutomated = []
            this.bulkOpRecordMessage = []
          })
        }
      },
      queryType(type){
        if (type === 'DocumentResponse') {
          return 'inbox'
        } else if (type === 'DocumentRequest') {
          return 'sent'
        } else if (type === 'SentEmail') {
          return 'automated-emails'
        } else {
          return ''
        }
      },
      getContacts(params) {
        return this.listStore.getContacts(params)
      },
      getUsers(params) {
        return this.listStore.getUsers(params)
      },
      trashVideo(){
        if(this.locale === 'de')
          return 'https://relatico.wistia.com/medias/fuzu9lzsvh'
        return 'https://relatico.wistia.com/medias/l5ggu10fft'
      }
    }
  }

</script>

<style lang="sass" scoped>
  .actions-on-table
    float: left
    width: 100%
  .filter-all-messages
    form
      text-align: left
      label
        text-align: left
        display: block
      .form-group
        margin-bottom: 20px
        .date
          width: 47%
          display: inline-block
          margin-right: 10px
      .form-inline
        label
          width: 20%
          &:first-child
            width: 50%
          &:last-child
            width: 5%
            color: #686868
            cursor: pointer
  table
    td
      &.search-icon-column
        img
          width: 30px
          height: auto
          margin-left: 5px
  .bulk-container
    background-color: #EBF4FE
    display: flex
    align-items: center
    padding: 8px 0
    border-radius: 3px
    float: left
    width: 100%
    > span
      padding-left: 10px
    ul
      list-style: none
      padding: 0
      margin: 0
      display: flex
      li
        position: relative
        a
          &:hover, &:visited, &:focus
            background: none
            text-decoration: underline
    &.hide
      display: none
</style>
